<template>
  <div v-if="isOpen" class="fixed inset-0 bg-gray-50 md:bg-black md:bg-opacity-50 z-50 flex items-start md:items-center justify-center" @click="onClose">
    <!-- Edit Profile popup -->
    <div class="bg-gray-50 md:bg-white md:rounded-2xl md:shadow-xl w-full h-full md:h-auto md:max-w-lg md:max-h-[80vh] md:mx-4 overflow-y-auto scrollbar-hide" @click.stop>
      <div class="p-6 pt-10 md:pt-6">
        <div class="flex justify-between items-center mb-8 md:mb-6">
          <h2 class="text-2xl font-bold nav-font">Edit Profile</h2>
          <button @click="onClose" class="text-gray-500 hover:text-gray-700 p-2">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form @submit.prevent="handleUpdateProfile" class="space-y-5">
          <!-- Profile Photo Upload -->
          <div class="flex flex-col items-center mb-2">
            <div class="relative mb-3">
              <div class="w-24 h-24 md:w-20 md:h-20 rounded-full bg-gray-50 border-2 border-gray-200 overflow-hidden">
                <img
                  :src="formData.picture || user.picture"
                  alt="Profile"
                  class="w-full h-full object-cover"
                />
              </div>
              <div class="absolute bottom-0 right-0 bg-blue-500 rounded-full w-6 h-6 flex items-center justify-center text-white cursor-pointer">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </div>
            </div>
            <p class="text-sm text-gray-500">Upload a new photo</p>
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2">Name</label>
            <input
              type="text"
              v-model="formData.username"
              class="w-full px-4 py-4 md:py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-base md:text-sm"
              placeholder="Enter your full name"
            />
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2">Email</label>
            <input
              type="email"
              v-model="formData.email"
              class="w-full px-4 py-4 md:py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-base md:text-sm bg-gray-100"
              placeholder="Enter your email"
              disabled
            />
            <p class="text-xs text-gray-500 mt-1"></p>
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2">Bio</label>
            <textarea
              v-model="formData.bio"
              rows="4"
              class="w-full px-4 py-4 md:py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-base md:text-sm"
              placeholder="Tell us about yourself"
            ></textarea>
          </div>

          <!-- Form Buttons -->
          <div class="flex justify-between md:justify-end space-x-3 pt-8 md:pt-4 pb-6 md:pb-4">
            <button
              type="button"
              @click="onClose"
              class="flex-1 md:flex-none px-5 py-3 md:py-2 bg-gray-100 text-gray-700 rounded-full hover:bg-gray-200 transition-colors"
            >
              Cancel
            </button>
            <button
              type="button"
              class="flex-1 md:flex-none px-5 py-3 md:py-2 bg-gray-900 text-white rounded-full hover:bg-gray-800 transition-colors"
            >
              Save Changes
            </button>
          </div>

          <!-- Account Actions Section -->
          <div class="border-t border-gray-200 mt-2 pt-8">
            <h3 class="text-lg font-semibold text-gray-900 mb-4">Account Actions</h3>

            <!-- Logout Button -->
            <button
              type="button"
              @click="handleLogout"
              class="w-full mb-3 px-5 py-3 md:py-2 bg-gray-100 text-gray-700 rounded-full hover:bg-gray-200 transition-colors flex items-center justify-center"
            >
              <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
              Logout
            </button>

            <!-- Delete Account Button -->
            <button
              type="button"
              @click="confirmDeleteAccount"
              class="w-full px-5 py-3 md:py-2 bg-red-50 text-red-600 rounded-full hover:bg-red-100 transition-colors flex items-center justify-center"
            >
              <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
              Delete Account
            </button>
          </div>

          <div class="pb-10 md:pb-0"></div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import { reactive, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useStore } from 'vuex'

export default {
  name: 'EditProfilePopup',
  props: {
    isOpen: {
      type: Boolean,
      required: true
    },
    user: {
      type: Object,
      required: true
    }
  },
  emits: ['close', 'update'],
  setup(props, { emit }) {
    const router = useRouter()
    const store = useStore()

    const formData = reactive({
      username: props.user.name,
      email: props.user.email,
      bio: props.user.bio || 'Hello, I really like mountain climbing. 🏔️',
      picture: props.user.picture || ''
    })

    // Update form data when user prop changes
    watch(() => props.user, (newUser) => {
      formData.username = newUser.name
      formData.email = newUser.email
      formData.bio = newUser.bio || 'Hello, I really like mountain climbing. 🏔️'
      formData.picture = newUser.picture || ''
    })

    // Handle profile update
    const handleUpdateProfile = () => {
      const updatedData = {
        name: formData.username,
        bio: formData.bio,
        // Email cannot be changed for Google OAuth users
        // email: formData.email,
        picture: formData.picture
      }

      // Update the profile in parent component
      emit('update', updatedData)

      // Close the edit profile popup
      emit('close')
    }

    // Handle logout
    const handleLogout = async () => {
      try {
        await store.dispatch('logout')
        emit('close')
        router.push('/login')
      } catch (error) {// Even if there's an error, try to redirect to login
        router.push('/login')
      }
    }

    // Show delete account confirmation
    const confirmDeleteAccount = () => {
      if (confirm('Are you sure you want to delete your account ?')) {
        deleteAccount()
      }
    }

    // Delete account
    const deleteAccount = async () => {
      try {
        // Call the delete user API
        await store.dispatch('deleteAccount')
        emit('close')
        router.push('/login')
      } catch (error) {alert('Failed to delete account. Please try again later.')
      }
    }

    const onClose = () => {
      emit('close')
    }

    return {
      formData,
      handleUpdateProfile,
      handleLogout,
      confirmDeleteAccount,
      onClose
    }
  }
}
</script>

<style scoped>
.nav-font {
  font-family: 'Poppins', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Open Sans', 'Helvetica Neue', sans-serif;
}

/* Smooth transitions */
.transition-colors {
  transition: all 0.2s ease;
}

/* Custom focus styles */
input:focus, textarea:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.2);
}

/* Hide scrollbar but keep scrolling functionality */
.scrollbar-hide {
  -ms-overflow-style: none;  /* IE and Edge */
  scrollbar-width: none;     /* Firefox */
}

.scrollbar-hide::-webkit-scrollbar {
  display: none;             /* Chrome, Safari and Opera */
}
</style>
